// Isomorphic API request helper — the single source of truth for response validation.
//
// It deliberately imports NO logger: it runs in both server actions and client (react-query)
// code, and the server logger is `server-only`. Detection lives here; the error-level LOGGING is
// done once by the caller's handler (`safe`/`logged` on the server, the client logger for the one
// browser query) so secrets never cross the server/client boundary.

export class ApiError extends Error {
    readonly status: number;
    readonly url: string;
    readonly body: string;

    constructor(status: number, url: string, body: string) {
        super(`API request failed: ${status} ${url}`);
        this.name = 'ApiError';
        this.status = status;
        this.url = url;
        this.body = body;
    }
}

/**
 * Performs the request and returns the parsed JSON, throwing {@link ApiError} when the response is
 * not OK or when a GraphQL `errors` envelope is present. Replaces the previous pattern of
 * `(await (await fetch(...)).json())` which assumed every response was a success.
 */
export async function apiFetch<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
    const res = await fetch(input, init);
    const url = String(input);

    if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new ApiError(res.status, url, body);
    }

    // Some endpoints answer with an empty body (e.g. 204, or the address/favourite mutations that
    // return ''). Don't force JSON parsing on those — only the OK status matters there.
    const text = await res.text();
    if (!text) {
        return undefined as T;
    }

    let json: T;
    try {
        json = JSON.parse(text) as T;
    } catch {
        return text as unknown as T;
    }

    // GraphQL transports a 200 with an `errors` array — treat that as a failure too.
    const errors = (json as { errors?: unknown })?.errors;
    if (Array.isArray(errors) && errors.length > 0) {
        throw new ApiError(res.status, url, JSON.stringify(errors));
    }

    return json;
}
